import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Icon, LinkButton } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { useBackCta } from 'src/hooks';
import { useContinueToNextPathStep } from 'src/hooks/useContinueToNextPathStep';
import useSafePush from 'src/hooks/useSafePush';
import { PreCheckpointScreen } from 'src/apps/Path/PreCheckpointScreen';
import { PathGoBackButton } from 'src/apps/Path/PathContinueButtons';
import { LearnablePronunciationData } from 'src/api/userDictionary/types';
import { PronunciationState } from '../../hooks/usePronunciationSession';
import { useSessionSpeakerClips } from '../../hooks/useSessionSpeakerClips';
import { SpeakerMosaic } from '../SpeakerMosaic';
import soundFile from '../assets/activity_finished.mp3';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'pronunciation_session_eos_title',
    defaultMessage: 'Perfect!',
  },
  description: {
    id: 'pronunciation_session_eos_description',
    defaultMessage:
      "Takes courage to do what you did, the more you do it, the more you'll sound like your locals.",
  },
  practiced: {
    id: 'pronunciation_session_eos_practiced',
    defaultMessage: 'Practiced {count}',
  },
  continueToNextLesson: {
    id: 'pronunciation_session_eos_continue',
    defaultMessage: 'Continue to next lesson',
  },
  pronounceMoreWords: { id: 'learnq_eosCta_pronounceMoreWords' },
  continueLearning: { id: 'path_continue_learning_cta', defaultMessage: 'Continue learning' },
  backToMyPath: { id: 'blob_pronunciation_back_to_path', defaultMessage: 'Back to my path' },
});

/**
 * SPIKE: end of session on the same speaker mosaic as the intro, so the
 * session opens and closes on the faces the learner practised with.
 */
export const ImmersiveSessionCompletedView: FC<{
  learnables: LearnablePronunciationData[];
  pronunciationStates: PronunciationState[];
  onRestart: () => void;
  pathwayNodeId?: number | null;
  languagePairId?: number | null;
  onContinueLearning?: () => void;
}> = ({
  learnables,
  pronunciationStates,
  onRestart,
  pathwayNodeId = null,
  languagePairId = null,
  onContinueLearning,
}) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const backCta = useBackCta(ROUTES.ai.buddies);
  const speakerClips = useSessionSpeakerClips(learnables);

  const {
    continueToNextStep,
    isResolving,
    pendingCheckpointUrl,
    confirmCheckpoint,
    isLaunchingCheckpoint,
  } = useContinueToNextPathStep({
    languagePairId,
    pathwayNodeId,
    pathwayCheckpointId: null,
    sessionType: 'pronunciation',
  });

  const practisedCount = pronunciationStates.filter(s => s.result === 'completed').length;
  const resultFor = (learnableIdKey: number) =>
    pronunciationStates.find(s => s.learnableIdKey === learnableIdKey)?.result;

  /* istanbul ignore if: spike */
  if (pendingCheckpointUrl && languagePairId) {
    return (
      <PreCheckpointScreen
        onContinue={confirmCheckpoint}
        isContinuing={isLaunchingCheckpoint}
        onBack={() => safePush(ROUTES.path)}
      />
    );
  }

  return (
    <S.Screen>
      <SpeakerMosaic clips={speakerClips} />
      {/* decorative sound SFX, captions not applicable */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio autoPlay preload="auto" src={soundFile} data-testid="session-completed-sound" />

      <S.Panel>
        <S.Card>
          <S.Avatar>
            {speakerClips[0] && (
              <S.AvatarVideo
                src={`${speakerClips[0]}#t=0.5`}
                muted
                playsInline
                preload="metadata"
              />
            )}
          </S.Avatar>
          <S.Title data-testid="SessionCompleted-title">{intl.formatMessage(msgs.title)}</S.Title>
          <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
          <S.PractisedPill data-testid="SessionCompleted-phraseCount">
            <Icon name="lightning" />
            {intl.formatMessage(msgs.practiced, { count: practisedCount })}
          </S.PractisedPill>

          <S.WordList>
            {learnables.map(learnable => {
              const result = resultFor(learnable.learnableIdKey);
              return (
                <S.WordRow key={learnable.learnableIdKey}>
                  <S.WordTick
                    name={result === 'completed' ? 'tickFilled' : 'tickOutline'}
                    $isComplete={result === 'completed'}
                  />
                  <S.WordTarget dir="auto">{learnable.mainTargetValue}</S.WordTarget>
                  <S.WordSource dir="auto">{learnable.sourceDefinitionValue}</S.WordSource>
                </S.WordRow>
              );
            })}
          </S.WordList>

          <S.Actions>
            {onContinueLearning ? (
              <>
                <Button buttonType="primary" buttonSize="large" wide onClick={onContinueLearning}>
                  {intl.formatMessage(msgs.continueLearning)}
                </Button>
                <LinkButton buttonType="secondary" buttonSize="large" wide href={ROUTES.path}>
                  {intl.formatMessage(msgs.backToMyPath)}
                </LinkButton>
              </>
            ) : (
              <>
                {pathwayNodeId !== null && (
                  <Button
                    buttonType="primary"
                    buttonSize="large"
                    wide
                    loading={isResolving}
                    disabled={isResolving}
                    onClick={continueToNextStep}
                    data-testid="path-continue-learning-button"
                  >
                    {intl.formatMessage(msgs.continueToNextLesson)}
                  </Button>
                )}
                <S.SecondaryActions>
                  <Button buttonType="secondary" buttonSize="large" wide onClick={onRestart}>
                    {intl.formatMessage(msgs.pronounceMoreWords)}
                  </Button>
                  {pathwayNodeId !== null ? (
                    <PathGoBackButton />
                  ) : (
                    <LinkButton buttonType="secondary" buttonSize="large" wide href={backCta.href}>
                      {backCta.text}
                    </LinkButton>
                  )}
                </S.SecondaryActions>
              </>
            )}
          </S.Actions>
        </S.Card>
      </S.Panel>
    </S.Screen>
  );
};
